import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Sparkles, RefreshCw } from "lucide-react";
import { useLang } from "@/i18n";
import { ProgereReleaseService } from "@/services/progereReleases";
import { catMeta, CATEGORY_ORDER, pick } from "@/progere-design-system/releaseCategories";
import { PROGERE_MODULE } from "@/config/progereModule";

// Fenêtre « Nouveautés » — s'affiche une fois par utilisateur et par version.
export function ProgereReleasePopup() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [rel, setRel] = useState(null);

  useEffect(() => {
    let alive = true;
    ProgereReleaseService.unseen()
      .then((r) => {
        if (!alive || !r) return;
        setRel(r);
        ProgereReleaseService.markSeen(r.id).catch(() => {});
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const grouped = useMemo(() => {
    if (!rel) return [];
    const by = {};
    (rel.items || []).forEach((it) => { (by[it.category] ||= []).push(it); });
    return CATEGORY_ORDER.filter((c) => by[c]?.length).map((c) => ({ cat: c, items: by[c] }));
  }, [rel]);

  if (!rel) return null;

  const isEn = lang === "en";
  const title = pick(rel, "title", lang) || (isEn ? "New version" : "Nouvelle version");
  const desc = pick(rel, "description", lang);
  const mandatory = !!rel.mandatory_update;

  const close = async () => {
    try { await ProgereReleaseService.acknowledge(rel.id); } catch {}
    setRel(null);
  };
  const openHistory = async () => { await close(); navigate("/nouveautes"); };
  const reloadApp = async () => { await close(); window.location.reload(); };

  return (
    <div className="progere-release-overlay" data-testid="release-popup" role="dialog" aria-modal="true">
      <div className="progere-release-popup">
        {/* En-tête */}
        <div className="p-6 pb-4 sticky top-0" style={{ background: "var(--pg-bg-card)", borderBottom: "1px solid var(--pg-border)" }}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="progere-release-version" data-testid="release-version">
                <Sparkles size={12} /> {PROGERE_MODULE.fullName} · v{rel.version}
              </span>
              <h2 className="font-display font-bold tracking-tight mt-3 text-2xl leading-tight">
                <span style={{ color: "var(--pg-text-primary)" }}>{isEn ? "NEW " : "NOUVELLE "}</span>
                <span style={{ color: "var(--pg-gold)" }}>{isEn ? "VERSION" : "VERSION"}</span>
              </h2>
              <p className="text-sm mt-1" style={{ color: "var(--pg-text-secondary)" }}>{title}</p>
              {rel.published_at && (
                <p className="text-xs mt-1" style={{ color: "var(--pg-text-muted)" }}>
                  {new Date(rel.published_at).toLocaleDateString(isEn ? "en-CA" : "fr-CA", { year: "numeric", month: "long", day: "numeric" })}
                </p>
              )}
            </div>
            {!mandatory && (
              <button onClick={close} data-testid="release-close" aria-label="Fermer"
                style={{ color: "var(--pg-text-muted)" }} className="hover:opacity-70 transition-opacity">
                <X size={20} />
              </button>
            )}
          </div>
          {desc && <p className="text-sm mt-3" style={{ color: "var(--pg-text-secondary)" }}>{desc}</p>}
          {mandatory && (
            <div className="mt-3 text-xs font-semibold px-3 py-2 rounded-lg"
              style={{ background: "color-mix(in srgb, var(--pg-red) 14%, transparent)", color: "var(--pg-red)" }}>
              {isEn ? "Required update — please refresh to continue." : "Mise à jour requise — veuillez actualiser pour continuer."}
            </div>
          )}
        </div>

        {/* Contenu par catégorie */}
        <div className="p-6 pt-4 space-y-5">
          {grouped.map(({ cat, items }) => {
            const m = catMeta(cat);
            const Icon = m.icon;
            return (
              <div key={cat} data-testid={`release-group-${cat}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={15} style={{ color: m.color }} />
                  <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: m.color }}>
                    {isEn ? m.labelEn : m.labelFr}
                  </span>
                </div>
                <div className="space-y-2">
                  {items.map((it, i) => {
                    const it_title = pick(it, "title", lang);
                    const it_desc = pick(it, "description", lang);
                    return (
                      <div key={i} className="progere-release-item">
                        <span className="progere-release-badge mt-0.5"
                          style={{ color: m.color, background: "color-mix(in srgb, currentColor 14%, transparent)" }}>
                          {isEn ? m.en : m.fr}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium" style={{ color: "var(--pg-text-primary)" }}>{it_title}</p>
                          {it_desc && <p className="text-xs mt-0.5" style={{ color: "var(--pg-text-muted)" }}>{it_desc}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="p-6 pt-2 flex flex-col sm:flex-row gap-2 sm:justify-end sticky bottom-0"
          style={{ background: "var(--pg-bg-card)", borderTop: "1px solid var(--pg-border)" }}>
          <button onClick={openHistory} data-testid="release-see-all"
            className="rounded-full px-4 py-2 text-sm font-semibold border transition-colors"
            style={{ borderColor: "var(--pg-border-strong)", color: "var(--pg-text-secondary)" }}>
            {isEn ? "See all release notes" : "Voir toutes les nouveautés"}
          </button>
          {mandatory ? (
            <button onClick={reloadApp} data-testid="release-update-now" className="btn-gold rounded-full px-4 py-2 text-sm flex items-center justify-center gap-2">
              <RefreshCw size={14} /> {isEn ? "Update now" : "Mettre à jour maintenant"}
            </button>
          ) : (
            <button onClick={close} data-testid="release-continue" className="btn-gold rounded-full px-5 py-2 text-sm font-semibold">
              {isEn ? `Continue to ${PROGERE_MODULE.fullName}` : `Continuer vers ${PROGERE_MODULE.fullName}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
